import React, { useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { RegisterRequestFileInterface } from '../registerRequestApi';
import {
  getRegisterRequestUploadView,
  RegisterRequestUploadViewResponse,
} from '../../RegisterUpload/registerUploadApi';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type Props = {
  registerRequestId: number;
  files: RegisterRequestFileInterface[];
  initialIndex?: number;
  setAllFilesChecked: (checked: boolean) => void;
};

export default function FileViewer({ registerRequestId, files, setAllFilesChecked, initialIndex = 0 }: Props) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentFile, setCurrentFile] = useState<RegisterRequestUploadViewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [imageRotation, setImageRotation] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfScale, setPdfScale] = useState(1);

  const selected = files[currentIndex] ?? null;
  const totalFiles = files.length;

  useEffect(() => {
    if (currentIndex >= files.length) {
      setCurrentIndex(0);
    }

    if (currentIndex === files.length - 1) {
      setAllFilesChecked(true);
    }
  }, [files.length, currentIndex]);

  useEffect(() => {
    if (!selected) return;

    let cancelled = false;

    async function loadCurrentFile() {
      try {
        setLoading(true);
        setError(null);
        setCurrentFile(null);
        setNumPages(0);
        setPdfPage(1);
        setPdfScale(1);
        setImageRotation(0);

        const data = await getRegisterRequestUploadView(registerRequestId, selected.id);

        if (!cancelled) {
          setCurrentFile(data);
        }
      } catch {
        if (!cancelled) {
          setError('Could not load file');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCurrentFile();

    return () => {
      cancelled = true;
    };
  }, [registerRequestId, selected?.id]);

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < totalFiles - 1;

  const title = useMemo(() => {
    if (!selected) return 'No file selected';
    return selected.filename;
  }, [selected]);

  if (!files.length) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600">No files available.</div>;
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-slate-900">{title}</div>
          <div className="text-sm text-slate-500">
            Fil {currentIndex + 1} av {totalFiles}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => canGoPrevious && setCurrentIndex((i) => i - 1)}
            disabled={!canGoPrevious}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
          >
            Föreg. fil
          </button>

          <button
            type="button"
            onClick={() => canGoNext && setCurrentIndex((i) => i + 1)}
            disabled={!canGoNext}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
          >
            Nästa fil
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
        {loading && (
          <div className="flex min-h-[500px] items-center justify-center text-slate-500">Loading file...</div>
        )}

        {error && <div className="flex min-h-[500px] items-center justify-center text-red-600">{error}</div>}

        {!loading && !error && currentFile?.mime_type === 'application/pdf' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
                disabled={pdfPage <= 1}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                Föreg. sida
              </button>

              <button
                type="button"
                onClick={() => setPdfPage((p) => Math.min(numPages, p + 1))}
                disabled={pdfPage >= numPages}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                Nästa sida
              </button>

              <span className="ml-2 text-sm text-slate-600">
                Sida {pdfPage} av {numPages || '—'}
              </span>

              <div className="mx-2 h-5 w-px bg-slate-300" />

              <button
                type="button"
                onClick={() => setPdfScale((s) => Math.max(0.6, +(s - 0.2).toFixed(2)))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                -
              </button>

              <button
                type="button"
                onClick={() => setPdfScale((s) => Math.min(3, +(s + 0.2).toFixed(2)))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                +
              </button>

              <span className="text-sm text-slate-600">{Math.round(pdfScale * 100)}%</span>
            </div>

            <div className="overflow-auto rounded-xl border border-slate-200 bg-white p-4">
              <Document
                file={currentFile.url}
                loading="Rendering PDF..."
                onLoadSuccess={({ numPages: loadedNumPages }) => setNumPages(loadedNumPages)}
                onLoadError={() => setError('Failed to render PDF')}
              >
                <Page pageNumber={pdfPage} scale={pdfScale} />
              </Document>
            </div>
          </div>
        )}

        {!loading && !error && currentFile?.mime_type.startsWith('image/') && (
          <div className="flex flex-col gap-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setImageRotation((rotation) => (rotation + 90) % 360)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                aria-label="Rotera bilden 90 grader medurs"
              >
                Rotera 90°
              </button>
            </div>

            <div className="flex min-h-[500px] items-center justify-center overflow-auto rounded-xl border border-slate-200 bg-white p-8">
              <Zoom key={`${currentFile.url}-${imageRotation}`}>
                <img
                  src={currentFile.url}
                  alt={currentFile.filename}
                  className="max-h-[75vh] max-w-full cursor-zoom-in object-contain"
                  style={{
                    rotate: `${imageRotation}deg`,
                  }}
                />
              </Zoom>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
